import React, { useCallback, useEffect, useState } from 'react';
import 'leaflet/dist/leaflet.css';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
// Layout and Pages imports
import AppLayout from './layouts/AppLayout';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import ListPage from './pages/ListPage';
import RegisterPage from './pages/RegisterPage';
import RegisterChoicePage from './pages/RegisterChoicePage';
import MapPage from './pages/MapPage';
import SettingsPage from './pages/SettingsPage';
import ServiceDashboardPage from './pages/ServiceDashboardPage';
import ServicePlannerPage from './pages/ServicePlannerPage';
import ServiceProviderPublicProfilePage from './pages/ServiceProviderPublicProfilePage';
import EditServiceProfilePage from './pages/EditServiceProfilePage';
import RepairRequestDetailsPage from './pages/RepairRequestDetailsPage';
import NewRepairRequestPage from './pages/NewRepairRequestPage';
import ClientRequestsPage from './pages/ClientRequestsPage';
import MapLayout from './layouts/MapLayout';
import SuggestionsPage from './pages/SuggestionsPage';
import ReporterProductFeedbackPage from './pages/ReporterProductFeedbackPage';
import VerifyEmailPage from './pages/VerifyEmailPage'; // Import the new page
import ResetPasswordPage from './pages/ResetPasswordPage';
import AuthActionPage from './pages/AuthActionPage';
import SecurityPage from './pages/SecurityPage';
// Auth Guards
// Removed incorrect imports for ProtectedRoute and PublicRoute
import AdminRouteGuard from './components/Auth/AdminRouteGuard';
import ServiceRouteGuard from './components/Auth/ServiceRouteGuard'; // Import the new guard
// Admin Panel Imports
import AdminLayout from './layouts/AdminLayout';
import AdminDashboardPage from './pages/admin/AdminDashboardPage'; // Corrected path
import AdminRequestsPage from './pages/admin/AdminRequestsPage';
import AdminServiceProvidersPage from './pages/admin/AdminServiceProvidersPage';
import AdminAccountsPage from './pages/admin/AdminAccountsPage';
import AdminVerificationsPage from './pages/admin/AdminVerificationsPage'; // Corrected path
import AdminTestDataPage from './pages/admin/AdminTestDataPage'; // Import the new test data page
import EquipmentTypeManagementPage from './pages/admin/EquipmentTypeManagementPage';
import SpecializationManagementPage from './pages/admin/SpecializationManagementPage'; // Import the new page
import AdminSuggestionsPage from './pages/admin/AdminSuggestionsPage';
import AdminNotificationsPage from './pages/admin/AdminNotificationsPage';
import AdminProductFeedbackPage from './pages/admin/AdminProductFeedbackPage';
import ServiceEquipmentTypesPage from './pages/ServiceEquipmentTypesPage';
import {
  AdminCommunityPage,
  CommunityCategoryPage,
  CommunityHomePage,
  CommunityThreadPage,
  PublicCommunityPage,
} from './pages/community/CommunityPages';
import CommunityOutcomePage from './pages/community/CommunityOutcomePage';
import PushNotificationsBootstrap from './components/common/PushNotificationsBootstrap';
import { ProductFeedbackRuntimeProvider } from './components/ProductFeedback/ProductFeedbackRuntimeProvider';
import LegacyServiceProviderProfileRedirect from './components/ServiceProvider/LegacyServiceProviderProfileRedirect';
import { resolveDefaultAppLanding, resolvePostAuthRedirect } from './utils/authRedirect';
import { isAccountSuspended } from './utils/accessControl';
import { authService } from './services/authService';
import { UserRole } from './types/firestore';
import {
  consumePostAuthIntent,
  encodePostAuthIntentForQuery,
  savePostAuthIntent,
  savePostAuthIntentFromQueryValue,
} from './utils/postAuthIntent';

// Odczytaj klucz witryny ze zmiennych środowiskowych
const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || 'YOUR_DEFAULT_RECAPTCHA_KEY'; // Provide a fallback or handle missing key

// Note: The original LoadingScreen and Protected/PublicRoute logic might be better placed
// in separate files (e.g., components/Auth/ProtectedRoute.tsx) for better organization.
// For this example, we keep them here but assume they might be refactored.

const LoadingScreen: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-primary-dark-100">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-ratuj-300 border-t-transparent mb-4"></div>
      <p className="text-primary-light-200 font-medium">Ładowanie sesji...</p>
    </div>
  </div>
);

const SuspendedAccountScreen: React.FC = () => {
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [signOutError, setSignOutError] = useState<string | null>(null);

  const handleSignOut = useCallback(async () => {
    if (isSigningOut) {
      return;
    }
    setIsSigningOut(true);
    setSignOutError(null);
    try {
      await authService.logout();
    } catch (error) {
      console.error('[ProtectedRoute] Failed to sign out suspended account session:', error);
      setSignOutError('Nie udało się zakończyć sesji. Spróbuj ponownie.');
    } finally {
      setIsSigningOut(false);
    }
  }, [isSigningOut]);

  useEffect(() => {
    void handleSignOut();
  }, [handleSignOut]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-primary-dark-100 px-4">
      <div className="w-full max-w-md rounded-2xl border border-primary-dark-300/70 bg-primary-dark-200/90 p-6 text-center shadow-card">
        <h1 className="text-lg font-semibold text-primary-light-100">Konto zostało zawieszone</h1>
        <p className="mt-3 text-sm text-primary-light-300">
          Dostęp do chronionej części aplikacji jest zablokowany. Zaloguj się ponownie po
          odwieszeniu konta.
        </p>
        {signOutError && <p className="mt-3 text-sm text-status-error-500">{signOutError}</p>}
        <button
          type="button"
          onClick={() => {
            void handleSignOut();
          }}
          disabled={isSigningOut}
          className="mt-5 inline-flex items-center justify-center rounded-lg bg-ratuj-300 px-4 py-2 text-sm font-medium text-primary-dark-100 hover:bg-ratuj-400 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isSigningOut ? 'Wylogowywanie...' : 'Wyloguj się'}
        </button>
      </div>
    </div>
  );
};

const AuthenticatedAppLandingRoute: React.FC = () => {
  const { userProfile, claims } = useAuth();
  const defaultLanding = resolveDefaultAppLanding(userProfile, claims);

  if (defaultLanding === '/app') {
    return <MapPage />;
  }

  return <Navigate to={defaultLanding} replace />;
};

// ProtectedRoute (Internal implementation)
const ProtectedRouteInternal: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { currentUser, userProfile, claims, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!currentUser) {
    const isClientRequestFlow = location.pathname.startsWith('/app/request/new');
    const fullClientRequestPath = `${location.pathname}${location.search}${location.hash}`;
    const normalizedIntentPath = isClientRequestFlow
      ? savePostAuthIntent(fullClientRequestPath, 'protected-route')
      : null;
    const encodedIntent = normalizedIntentPath
      ? encodePostAuthIntentForQuery(normalizedIntentPath)
      : null;
    const loginPath = encodedIntent ? `/login?postAuthIntent=${encodedIntent}` : '/login';
    return (
      <Navigate
        to={loginPath}
        state={{
          from: location,
          ...(isClientRequestFlow
            ? {
                authIntent: 'client-action',
                registerPath: '/register/client',
              }
            : {}),
        }}
        replace
      />
    );
  }

  if (isAccountSuspended(userProfile, claims)) {
    return <SuspendedAccountScreen />;
  }

  if (!currentUser.emailVerified) {
    return (
      <Navigate to="/verify-email" state={{ email: currentUser.email, from: location }} replace />
    );
  }

  return children;
};

// PublicRoute (Internal implementation)
const PublicRouteInternal: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { currentUser, userProfile, claims, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }

  if (currentUser) {
    const queryParams = new URLSearchParams(location.search);
    const queryIntent = queryParams.get('postAuthIntent');
    if (queryIntent) {
      savePostAuthIntentFromQueryValue(queryIntent, 'public-route-query');
    }

    const isLoginRoute = location.pathname === '/login';
    if (isLoginRoute && !currentUser.emailVerified) {
      return children;
    }

    const from = (location.state as any)?.from;
    const persistedIntent = consumePostAuthIntent();
    const fallback = resolveDefaultAppLanding(userProfile, claims);
    const safeTarget = resolvePostAuthRedirect({
      from,
      intentPath: persistedIntent,
      fallback,
    });
    return <Navigate to={safeTarget} replace />;
  }

  return children;
};

function App() {
  // Log an error if the key is missing but don't block the app entirely in dev
  if (recaptchaSiteKey === 'YOUR_DEFAULT_RECAPTCHA_KEY') {
    console.error(
      'Klucz witryny reCAPTCHA (REACT_APP_RECAPTCHA_SITE_KEY) nie jest ustawiony w .env! Używanie klucza domyślnego.'
    );
  }

  return (
    <AuthProvider>
      <PushNotificationsBootstrap />
      <GoogleReCaptchaProvider reCaptchaKey={recaptchaSiteKey}>
        <Router>
          <ProductFeedbackRuntimeProvider>
            <Routes>
              {/* Public Routes (unauthenticated only) */}
              <Route
                path="/"
                element={
                  <PublicRouteInternal>
                    <LandingPage />
                  </PublicRouteInternal>
                }
              />
              <Route
                path="/login"
                element={
                  <PublicRouteInternal>
                    <LoginPage />
                  </PublicRouteInternal>
                }
              />
              <Route
                path="/register"
                element={
                  <PublicRouteInternal>
                    <RegisterChoicePage />
                  </PublicRouteInternal>
                }
              />
              <Route
                path="/register/client"
                element={
                  <PublicRouteInternal>
                    <RegisterPage role={UserRole.CLIENT} showGoogleAuth />
                  </PublicRouteInternal>
                }
              />
              <Route
                path="/register/service"
                element={
                  <PublicRouteInternal>
                    <RegisterPage role={UserRole.SERVICE_ADMIN} />
                  </PublicRouteInternal>
                }
              />
              {/* Verify Email Route (accessible after registration attempt) */}
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/auth/action" element={<AuthActionPage />} />
              {/* Public Service Profile (accessible to all) */}
              {/* Redirect old path to new path */}
              <Route
                path="/service/:serviceId" // Keep the old path pattern
                element={<LegacyServiceProviderProfileRedirect />}
              />
              {/* --- NEW PUBLIC ROUTES within AppLayout --- */}
              <Route element={<AppLayout />}>
                {' '}
                {/* Wrap public browse routes in AppLayout */}
                <Route path="/map" element={<MapPage />} />
                <Route path="/list" element={<ListPage />} />
                <Route path="/community" element={<PublicCommunityPage />} />
                <Route path="/bezpieczenstwo" element={<SecurityPage />} />
                <Route path="/security" element={<Navigate to="/bezpieczenstwo" replace />} />
                <Route
                  path="/service-provider/:providerId"
                  element={<ServiceProviderPublicProfilePage />}
                />
              </Route>
              {/* --- END NEW PUBLIC ROUTES --- */}
              {/* --- PROTECTED ROUTES (authenticated only) - nested under /app --- */}
              <Route
                path="/app"
                element={
                  <ProtectedRouteInternal>
                    <AppLayout />
                  </ProtectedRouteInternal>
                }
              >
                {/* Index route for /app renders MapLayout */}
                <Route element={<MapLayout />}>
                  {' '}
                  {/* Remove index from layout route */}
                  {/* Index route within MapLayout resolves role-based default landing for generic /app */}
                  <Route index element={<AuthenticatedAppLandingRoute />} />
                  <Route path="discover" element={<MapPage />} />
                </Route>

                {/* List route (under AppLayout) */}
                <Route path="list" element={<ListPage />} />

                {/* General authenticated routes */}
                <Route path="profile" element={<Navigate to="/app/settings" replace />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="my-requests" element={<ClientRequestsPage />} />
                <Route path="community" element={<CommunityHomePage />} />
                <Route path="community/outcomes" element={<CommunityOutcomePage />} />
                <Route path="community/thread/:threadId" element={<CommunityThreadPage />} />
                <Route path="community/:categorySlug" element={<CommunityCategoryPage />} />
                <Route path="suggestions" element={<SuggestionsPage />} />
                <Route path="product-feedback" element={<ReporterProductFeedbackPage />} />
                <Route path="request/new" element={<NewRepairRequestPage />} />
                <Route path="request/new/:serviceId" element={<NewRepairRequestPage />} />
                <Route path="request/:requestId" element={<RepairRequestDetailsPage />} />

                {/* Routes for service module (onboarding + approved operational panel) */}
                <Route element={<ServiceRouteGuard />}>
                  <Route path="service" element={<ServiceDashboardPage />} />
                  <Route path="service/planner" element={<ServicePlannerPage />} />
                  <Route path="service/equipment-types" element={<ServiceEquipmentTypesPage />} />
                  <Route path="dashboard" element={<Navigate to="/app/service" replace />} />
                  <Route path="service-profile" element={<EditServiceProfilePage />} />
                  {/* Add other service-provider-only routes here */}
                </Route>
              </Route>{' '}
              {/* End of /app protected routes */}
              {/* --- ADMIN ROUTES (Protected by AdminRouteGuard) --- */}
              <Route element={<AdminRouteGuard />}>
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<AdminDashboardPage />} />
                  <Route path="requests" element={<AdminRequestsPage />} />
                  <Route path="service-providers" element={<AdminServiceProvidersPage />} />
                  <Route path="accounts" element={<AdminAccountsPage />} />
                  <Route path="notifications" element={<AdminNotificationsPage />} />
                  <Route path="product-feedback" element={<AdminProductFeedbackPage />} />
                  <Route path="community" element={<AdminCommunityPage />} />
                  <Route path="verifications" element={<AdminVerificationsPage />} />
                  <Route path="specializations" element={<SpecializationManagementPage />} />
                  <Route path="equipment-types" element={<EquipmentTypeManagementPage />} />
                  <Route path="suggestions" element={<AdminSuggestionsPage />} />
                  <Route path="tools" element={<AdminTestDataPage />} />
                  <Route path="test-data" element={<Navigate to="/admin/tools" replace />} />
                  {/* <Route path="orders" element={<AdminOrdersPage />} /> */}
                </Route>
              </Route>{' '}
              {/* End of /admin protected routes */}
              {/* --- Redirect unknown paths --- */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </ProductFeedbackRuntimeProvider>
        </Router>
      </GoogleReCaptchaProvider>
    </AuthProvider>
  );
}

export default App;
